import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  DialogFooter,
  Form,
  FormDatePicker,
  FormField,
  FormInput,
  FormSelect,
  FormToggleGroup,
  useMultiStepForm,
} from "web-design-system";
import {
  useCountryOptions,
  useCreateInvestorProfileDetails,
  useInvestorProfile,
  useUpdateInvestorProfileDetails,
} from "creso-core/hooks";
import { getMinorDetailsSchema } from "creso-core/zod";
import {
  GENDER,
  INCOME_RANGE,
  MINOR_FINANCIAL_PROFILE_DEFAULTS,
  OCCUPATION,
  PEP_STATUS,
  SOURCE_OF_WEALTH,
} from "creso-core/constants";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";

const labelOf = (options: { label: string; value: string }[], value: string) =>
  options.find((o) => o.value === value)?.label ?? value;

/** Minor onboarding step 1: the minor's details and a default financial profile */
export function MinorDetails() {
  const { client } = useClientOnboarding();
  const { nextStep } = useMultiStepForm();
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  const { data: profile } = useInvestorProfile(
    { investment_account_guid: client?.investment_account ?? "" },
    { enabled: true }
  );
  const { mutateAsync: createProfile } = useCreateInvestorProfileDetails();
  const { mutateAsync: updateProfile } = useUpdateInvestorProfileDetails();
  const countryOptions = useCountryOptions();

  const firstName = client?.name?.split(" ")[0] ?? "";
  const schema = useMemo(() => getMinorDetailsSchema(firstName), [firstName]);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    values: {
      name: profile?.name || client?.name || "",
      gender: profile?.gender ?? "",
      date_of_birth: profile?.date_of_birth ?? "",
      country_of_birth: profile?.country_of_birth ?? "IN",
      occupation:
        profile?.occupation ?? MINOR_FINANCIAL_PROFILE_DEFAULTS.occupation,
      income_slab:
        profile?.income_slab ?? MINOR_FINANCIAL_PROFILE_DEFAULTS.income_slab,
      source_of_wealth:
        profile?.source_of_wealth ??
        MINOR_FINANCIAL_PROFILE_DEFAULTS.source_of_wealth,
      pep_details:
        profile?.pep_details ?? MINOR_FINANCIAL_PROFILE_DEFAULTS.pep_details,
    },
  });

  const [occupation, incomeSlab, sourceOfWealth, pepDetails] = form.watch([
    "occupation",
    "income_slab",
    "source_of_wealth",
    "pep_details",
  ]);

  const onSubmit = async (data: z.infer<typeof schema>) => {
    try {
      const payload = {
        ...data,
        investment_account_guid: client?.investment_account ?? "",
      };
      if (profile) {
        await updateProfile({
          ...payload,
          client_id: client?.id,
          holder_role: "primary",
        });
      } else {
        await createProfile({ ...payload, tax_status: client?.tax_status });
      }
      nextStep();
    } catch (error) {
      handleError(error, "Failed to save minor details");
    }
  };

  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Minor details</span>
      <Form {...form}>
        <form
          // Padding keeps focus rings from being clipped by the scroll area
          className="flex flex-col flex-1 gap-4 overflow-y-auto -m-1 p-1"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormInput {...field} label="Name of minor" disabled />
            )}
          />
          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormToggleGroup
                label="Gender"
                options={GENDER.map((g) => ({
                  label: g.label,
                  value: g.value,
                }))}
                {...field}
              />
            )}
          />
          <div className="flex w-full items-start gap-4">
            <FormField
              control={form.control}
              className="flex-1"
              name="date_of_birth"
              render={({ field }) => (
                <FormDatePicker
                  label="Date of birth"
                  format="yyyy-MM-dd"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={(date) => date > new Date()}
                />
              )}
            />
            <FormField
              control={form.control}
              className="flex-1"
              name="country_of_birth"
              render={({ field }) => (
                <FormSelect
                  {...field}
                  label="Country of birth"
                  placeholder="Select country"
                  options={countryOptions}
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Financial profile</span>
            {isEditingProfile ? (
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="occupation"
                  render={({ field }) => (
                    <FormSelect
                      {...field}
                      label="Occupation"
                      options={OCCUPATION}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name="income_slab"
                  render={({ field }) => (
                    <FormSelect
                      {...field}
                      label="Income range"
                      options={INCOME_RANGE}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name="source_of_wealth"
                  render={({ field }) => (
                    <FormSelect
                      {...field}
                      label="Source of wealth"
                      options={SOURCE_OF_WEALTH}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name="pep_details"
                  render={({ field }) => (
                    <FormSelect
                      {...field}
                      label="Political exposure"
                      options={PEP_STATUS}
                    />
                  )}
                />
              </div>
            ) : (
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                <span className="text-muted-foreground">
                  {[
                    labelOf(OCCUPATION, occupation),
                    labelOf(INCOME_RANGE, incomeSlab),
                    labelOf(SOURCE_OF_WEALTH, sourceOfWealth),
                    labelOf(PEP_STATUS, pepDetails),
                  ].join(" · ")}
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={() => setIsEditingProfile(true)}
                >
                  Edit
                </Button>
              </div>
            )}
          </div>
          <DialogFooter className="mt-auto flex sm:flex-col gap-4">
            <Button
              className="w-full"
              type="submit"
              disabled={form.formState.isSubmitting}
            >
              Next
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
